import {
  TASK_STATUS_LABELS,
  ZEITFENSTER,
  ZEITFENSTER_LABELS,
  alterDerAufgabe,
  entstanden,
  inKeinerEbene,
  nurVorraete,
  ohneWunschImVorrat,
  wartetAufVorqualifizierung,
  zaehltAlsEntwickler,
  type TrichterAufgabe,
  type Zeitfenster,
} from '@netcon-work/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useCallback, useMemo, useState } from 'react'
import { poolsQuery, vorhabenQuery } from '../lib/queries'
import { cn } from '../lib/cn'
import { trpc } from '../lib/trpc'
import { AufgabenVerweis, useEscSchliesst } from './AufgabeKontext'
import { Button, Card, CardPad } from './ui/primitives'
import { nebenBestandVerwerfen } from '../lib/mutationen'
import { useAufgabenDieserWelt, usePersonenDieserWelt } from '../lib/welt'

/**
 * **Der Trichter** (Nils, 17.08.2026, [F-44](../../../../docs/OFFENE-FRAGEN.md)).
 *
 * > *„Großer Trichter oben mit den Pools (wie so eine umgedrehte Pyramide, wo unten in der
 * > Spitze der wichtigste Pool mit Anzahl der Aufgaben ist – Hover/Klick öffnet im Popup den
 * > Inhalt). Darunter dann die Ebene «zugewiesen». Darunter «in Arbeit», darunter «zur
 * > Abnahme/fertig»."*
 *
 * Und auf die Rückfrage, ob er die Pool-Ansicht ersetzt: *„Nein, er ersetzt auf keinen Fall
 * die Pools, es ist nur eine grafische Darstellung der Pools."*
 *
 * ## Was der Trichter zeigt, was die Pool-Ansicht nicht zeigt
 *
 * Den **Weg**. Die Pool-Ansicht beantwortet *„was liegt bereit"*; hier steht daneben, was
 * daraus geworden ist – zugewiesen, in Arbeit, zur Abnahme. Zusammen beantworten die Ebenen
 * die Frage, die ein Abteilungsleiter wirklich hat: *staut es sich, und wo?*
 *
 * ## Die Form ist die Wichtigkeit, die Füllung die Menge
 *
 * Nils, 18.08.2026: *„Kannst du das optisch wie eine umgedrehte Pyramide darstellen? (Sofort
 * ist unten, das ist das Wichtigste, Fertig ist oben, ist ja das Unwichtigste)"*
 *
 * Der erste Anlauf machte die **Breite** zur Menge. Als Aussage war das richtig – ein Stau
 * unten macht die Spitze breiter – und als Bild falsch: es sah aus wie ein Balkendiagramm,
 * und der Trichter ist der Punkt. Jetzt trägt jede Ebene beides: die **äußere Breite** nimmt
 * nach unten ab (die Pyramide, Wichtigkeit), die **Füllung darin** zeigt die Menge.
 *
 * ## Drei Änderungen vom 19.08.2026 ([E-131](../../../../docs/ENTSCHEIDUNGEN.md))
 *
 * Nils: *„fehlen da nicht noch die nicht zugewiesenen oder sind das dann die aus dem
 * Eingang?"* Die Nicht-Zugewiesenen sind die Pool-Ebenen. Beim Nachsehen kamen drei Sachen
 * heraus, die das Bild unbrauchbar machten:
 *
 * 1. **Der Zulauf fehlte.** Der Eingang ist die Ebene, an der sich am meisten staut, und er
 *    stand nicht im Bild. Er ist aber **keine Ebene der Pyramide** – er ist der Stoff, aus
 *    dem sie gemacht wird, und steht deshalb als eigene Zeile darüber.
 * 2. **Aufgaben fielen still heraus.** Wer in der Planung ein Zeitfenster setzt, ohne einen
 *    Pool zu setzen, nimmt die Aufgabe aus dem Eingang und stellt sie in keine Ebene. Die
 *    Regel dafür steht in `trichter.ts` im geteilten Paket, die Zahl unter der Spitze.
 * 3. **„Fertig" zerstörte den Maßstab.** 3.054 erledigte gegen 48 zugewiesene: die Füllung
 *    aller arbeitenden Ebenen war ein Strich. Erledigtes steht jetzt als **Zeile über** der
 *    Pyramide – oben, wie Nils es wollte, aber außerhalb des Maßstabs.
 *
 * ## Warum „fertig" vier Wochen bleibt
 *
 * Nils: *„Fertige bleiben sonst 4 Wochen darin, wenn ich sie nicht manuell abnehme … oder
 * manuell auf «schließen» drücke. Das sorgt dafür, dass man die Fertigen noch eine Zeit lang
 * sieht, aber nicht übertrieben lange."* Gerechnet wird **ab dem Datum der Erledigung** –
 * das genügt und braucht keinen zusätzlichen Zustand, den jede Auswertung mitdenken müsste.
 */

/** Wie lange Erledigtes in der Kopfzeile stehen bleibt. */
const FERTIG_TAGE = 28

/**
 * Wie viele Zeilen ein Popup höchstens zeigt (Leitplanke 4).
 *
 * Vorher unbegrenzt – und „Fertig" trug **3.054** Zeilen, die alle auf einmal in den Baum
 * gerendert wurden. Wer 3.000 Erledigte durchblättern will, ist in „Alle Aufgaben" richtig;
 * hier ist das Popup ein Blick hinein, kein Arbeitsmittel.
 */
const POPUP_MAX = 200

/**
 * Das Ziel der Zulauf-Zeile – als `string` und nicht als Literal.
 *
 * Die Routen entstehen in `router.tsx` aus einer Liste und sind nicht typregistriert; ein
 * Literal `"/eingang"` lehnt `Link` deshalb ab. Seitenleiste und ⌘K-Palette gehen denselben
 * Weg (`eintrag.path` aus `lib/navigation.ts`).
 */
const ZUM_EINGANG: string = '/eingang'

/**
 * Was eine Zeile im Popup trägt.
 *
 * **Alter und Zeitfenster kamen am 19.08.2026 dazu** – Nils, mit einem Bild der Pool-Liste:
 * *„hier müsste noch das Alter mit rein. Dann kann ich besser sortieren und die Zeiten
 * vergeben."* Zwei Sätze, zwei verschiedene Sachen: das Alter ist eine **Auskunft**, das
 * Zeitfenster eine **Handlung**. Beide gehören in dieselbe Zeile, weil die Entscheidung („das
 * liegt seit 300 Tagen – ab in *jetzt*") in einem Blick fällt.
 *
 * `sourceCreatedAt` **und** `createdAt`: das Alter rechnet `alterDerAufgabe` daraus, und eine
 * in Work angelegte Aufgabe hat in der Quelle nichts stehen.
 */
type Zeile = {
  id: string
  title: string
  status: string
  assigneeName: string | null
  sourceCreatedAt: string | Date | null
  createdAt: string | Date
  zeitfenster: string | null
}

export function Trichter() {
  /**
   * **Regale sind keine Ebene** (20.08.2026, [E-191](../../../docs/ENTSCHEIDUNGEN.md)).
   *
   * Der Trichter zeigt, wo Arbeit steht und wo sie hängt. Der Parkplatz ist beides nicht –
   * eine Stufe „🅿️ Parkplatz: 47" sähe aus wie ein Stau und wäre eine Ablage. Geparkte
   * Aufgaben sind ohnehin zurückgestellt und fallen damit aus jeder Zeile heraus; hier geht es
   * darum, dass der Pool selbst keine bekommt.
   */
  const { data: allePools = [] } = useQuery(poolsQuery)
  const { data: vorhaben = [] } = useQuery(vorhabenQuery)
  const pools = nurVorraete(allePools)
  /** Der Trichter gehört zum Eingang und ist damit gebunden (Z-07). */
  const { aufgaben } = useAufgabenDieserWelt()
  const [offen, setOffen] = useState<{
    titel: string
    aufgaben: Zeile[]
    /** Nur die Z-05-Liste bietet die Sammelaktion an – siehe {@link WunschSammelLeiste}. */
    wunschSetzen?: boolean
  } | null>(null)
  /** Esc schließt die Liste – aber erst, wenn kein Aufgabendetail davor steht. */
  const listeSchliessen = useCallback(() => setOffen(null), [])
  useEscSchliesst(listeSchliessen)

  const grenze = Date.now() - FERTIG_TAGE * 86_400_000

  /**
   * **Die Pool-Zugehörigkeit kommt aus den Pools selbst**, nicht aus einem zweiten Feld an
   * der Aufgabe: die Pool-Ebenen zeigen ohnehin genau diese Listen, und zwei Quellen für
   * dieselbe Zahl wären zwei Gelegenheiten, sie unterschiedlich zu beantworten.
   */
  const poolsJeAufgabe = new Map<string, number>()
  for (const p of pools) {
    for (const a of p.aufgaben) {
      poolsJeAufgabe.set(a.id, (poolsJeAufgabe.get(a.id) ?? 0) + 1)
    }
  }

  /** Die Angaben, aus denen `trichter.ts` die Einordnung ableitet – für beide Zahlen dieselben. */
  const einordnung = (a: {
    id: string
    status: string
    assigneeId: string | null
    zeitfenster: string | null
    hatWunsch: number
  }): TrichterAufgabe => ({
    status: a.status,
    assigneeId: a.assigneeId,
    zeitfenster: a.zeitfenster,
    hatWunsch: a.hatWunsch === 1,
    inPools: poolsJeAufgabe.get(a.id) ?? 0,
  })

  /**
   * **Zugewiesen heißt: hat jemanden, arbeitet aber noch nicht.** Zuweisen setzt bewusst
   * nicht auf „in Arbeit" (Phase 4, AP 4.6) – ohne diese Ebene wären solche Aufgaben
   * unsichtbar, und genau sie bleiben liegen.
   */
  const zugewiesen = aufgaben.filter(
    (a) => a.assigneeId !== null && (a.status === 'bereit' || a.status === 'eingang'),
  )
  const inArbeit = aufgaben.filter((a) => a.status === 'in_arbeit')
  const abnahme = aufgaben.filter((a) => a.status === 'review')
  const fertig = aufgaben.filter(
    (a) => a.status === 'erledigt' && new Date(a.updatedAt).getTime() >= grenze,
  )

  /** Der Zulauf: was auf Vorqualifizierung wartet – und was dabei aus dem Bild gefallen ist. */
  const imEingang = aufgaben.filter((a) => wartetAufVorqualifizierung(einordnung(a)))
  const durchgefallen = aufgaben.filter((a) => inKeinerEbene(einordnung(a)))

  /**
   * **Was im Vorrat liegt, ohne dass jemand dafür vorgesehen ist** (Z-05).
   *
   * Der Wunsch des **Vorhabens** zählt mit – er wirkt
   * ([E-108](../../../../docs/ENTSCHEIDUNGEN.md)). Er steht nicht im Aufgabenbestand, also wird
   * er hier aus den Vorhaben nachgeschlagen; eine zweite Spalte an `tasks.all` wäre dieselbe
   * Auskunft an zwei Stellen.
   */
  const vorhabenMitWunsch = new Set(
    vorhaben.filter((v) => (v.wunschPersonIds?.length ?? 0) > 0).map((v) => v.id),
  )
  const ohneWunsch = aufgaben.filter((a) =>
    ohneWunschImVorrat({
      status: a.status,
      assigneeId: a.assigneeId,
      inPools: poolsJeAufgabe.get(a.id) ?? 0,
      hatWunsch: a.hatWunsch === 1,
      vorhabenHatWunsch: a.vorhabenId !== null && vorhabenMitWunsch.has(a.vorhabenId),
    }),
  )
  const aeltestesImEingang = imEingang.reduce(
    (max: number | null, a) => Math.max(max ?? 0, alterDerAufgabe(a)),
    null,
  )

  /**
   * **Die Pool-Zeilen kommen aus dem Aufgabenbestand**, nicht aus der Pool-Antwort.
   *
   * `pools.all` liefert das **wirksame** Zeitfenster (mit dem Rückfall aufs Vorhaben, E-108) –
   * richtig für die Sortierung im Vorrat, falsch für ein Auswahlfeld: dort stünde ein geerbter
   * Wert, als hätte die Aufgabe ihn selbst. Der Bestand liegt ohnehin im Client (Leitplanke 2),
   * also wird die Zeile dort nachgeschlagen. Fällt die Suche aus – ein Mit-Owner sieht nicht
   * jede Aufgabe –, bleibt die Pool-Zeile stehen.
   */
  const aufgabeNachId = new Map(aufgaben.map((a) => [a.id, a as unknown as Zeile]))

  /** Die Pools in ihrer eigenen Reihenfolge – `sort_order` ist die vorhandene Wichtigkeit. */
  const poolStufen = pools.map((p) => ({
    titel: `${p.icon} ${p.label}`,
    aufgaben: p.aufgaben.map((a) => aufgabeNachId.get(a.id) ?? (a as unknown as Zeile)),
  }))

  /**
   * **Von oben nach unten: unwichtig zu wichtig.** Ganz unten die Spitze – der wichtigste
   * Pool. Die Pools kommen **umgekehrt** zu `sort_order`: die zählt aufsteigend nach
   * Wichtigkeit, und wichtig gehört hier nach unten.
   *
   * „Fertig" ist keine Stufe mehr, sondern die Zeile über der Pyramide – siehe Punkt 3 im
   * Kopfkommentar.
   */
  const stufen = [
    { titel: 'Zur Abnahme', aufgaben: abnahme as unknown as Zeile[] },
    { titel: 'In Arbeit', aufgaben: inArbeit as unknown as Zeile[] },
    { titel: 'Zugewiesen', aufgaben: zugewiesen as unknown as Zeile[] },
    ...[...poolStufen].reverse(),
  ]

  /**
   * Der Maßstab kommt aus den **arbeitenden** Ebenen. Mit „Fertig" darin (3.054 gegen 48)
   * war jede Füllung ein Strich – die Aussage „hier staut es sich" war nicht mehr ablesbar.
   */
  const groesste = Math.max(1, ...stufen.map((s) => s.aufgaben.length))
  const mehrfach = [...poolsJeAufgabe.values()].filter((n) => n > 1).length

  return (
    <>
      {/**
       * **Die Zulauf-Zeile** – der Eingang ist keine Ebene, sondern der Zulauf in die
       * Pyramide. Sie steht auch bei einer leeren Zahl da: „nichts wartet" ist die Auskunft,
       * um die es geht, und eine Zeile, die nur bei Arbeit erscheint, wird nicht gesucht.
       */}
      <Card className="mb-2">
        <CardPad className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[12.5px]">
          <span className="font-semibold">📥 Zulauf</span>
          {imEingang.length === 0 ? (
            <span className="text-ink2">
              Nichts wartet auf Vorqualifizierung – der Eingang ist leer.
            </span>
          ) : (
            <>
              <span className="text-ink2">
                <strong className="text-[15px]">{imEingang.length}</strong>{' '}
                {imEingang.length === 1 ? 'Aufgabe wartet' : 'Aufgaben warten'} auf
                Vorqualifizierung
              </span>
              {aeltestesImEingang !== null && (
                <span
                  className={
                    aeltestesImEingang > 60 ? 'font-semibold text-pool-sofort' : 'text-muted'
                  }
                >
                  ältestes {aeltestesImEingang} Tage
                </span>
              )}
            </>
          )}
          <Link to={ZUM_EINGANG} className="ml-auto text-accent-deep hover:underline">
            zum Eingang →
          </Link>
        </CardPad>
      </Card>

      <Card className="mb-4">
        <CardPad>
          {/**
           * **Erledigtes steht oben** (Nils: *„Fertig ist oben, ist ja das Unwichtigste"*) –
           * aber als Zeile und nicht als Ebene, weil es sonst den Maßstab der Pyramide
           * bestimmt, ohne zu ihr zu gehören.
           */}
          <button
            type="button"
            disabled={fertig.length === 0}
            onClick={() =>
              setOffen({
                titel: `Fertig (${FERTIG_TAGE} Tage)`,
                aufgaben: fertig as unknown as Zeile[],
              })
            }
            className="mb-2 flex w-full items-baseline gap-2 border-b border-line pb-2 text-left text-[12px] text-ink2 disabled:cursor-default"
            title={
              fertig.length === 0
                ? 'Nichts erledigt in diesem Zeitraum'
                : 'Klicken zeigt die Erledigten'
            }
          >
            <span>✓ Durchgelaufen</span>
            <strong className="tabular text-[14px] text-ink">{fertig.length}</strong>
            <span className="text-muted">in {FERTIG_TAGE} Tagen</span>
          </button>

          <div className="grid gap-1">
            {stufen.map((stufe, i) => {
              /**
               * **Die äußere Breite ist die Pyramide.** Von 100 % oben auf 34 % unten,
               * gleichmäßig – sie sagt nichts über Mengen, sondern über die Stellung im
               * Ablauf. Die Füllung darin sagt die Menge.
               */
              const aussen = 100 - (i / Math.max(1, stufen.length - 1)) * 66
              const fuellung = stufe.aufgaben.length / groesste
              const istPool = i >= stufen.length - poolStufen.length
              return (
                <button
                  key={stufe.titel}
                  type="button"
                  disabled={stufe.aufgaben.length === 0}
                  onClick={() => setOffen(stufe)}
                  className="group flex items-center gap-2.5 text-left disabled:cursor-default"
                  title={
                    stufe.aufgaben.length === 0
                      ? `${stufe.titel}: leer`
                      : `${stufe.titel}: ${stufe.aufgaben.length} – klicken zeigt den Inhalt`
                  }
                >
                  <span className="w-[150px] shrink-0 truncate text-right text-[12px] text-ink2">
                    {stufe.titel}
                  </span>
                  <span className="flex min-w-0 flex-1 justify-center">
                    <span
                      className={`relative block h-[28px] overflow-hidden rounded-md border ${
                        istPool ? 'border-accent/40 bg-accent-soft' : 'border-line2 bg-[#f3f2ef]'
                      }`}
                      style={{ width: `${aussen}%` }}
                    >
                      {/**
                       * Die Füllung sitzt **innen** und ist zentriert – so bleibt die
                       * Pyramidenform erhalten, und ein Stau zeigt sich als volle Ebene.
                       */}
                      <i
                        className={`absolute inset-y-0 left-1/2 block -translate-x-1/2 ${
                          istPool ? 'bg-accent' : 'bg-[#c9c4ba]'
                        } ${stufe.aufgaben.length > 0 ? 'group-hover:opacity-80' : ''}`}
                        style={{ width: `${fuellung * 100}%` }}
                      />
                      <span
                        className={`tabular relative flex h-full items-center justify-center text-[12px] font-semibold ${
                          fuellung > 0.45 ? 'text-white' : 'text-ink2'
                        }`}
                      >
                        {stufe.aufgaben.length}
                      </span>
                    </span>
                  </span>
                </button>
              )
            })}
          </div>

          {/**
           * **Was durch die Spitze fällt.** Nur wenn es welche gibt – eine Zeile mit „0"
           * wäre eine Warnung ohne Anlass, und sie stünde dauerhaft da. Steht sie aber da,
           * ist sie eine Arbeitsanweisung: diesen Aufgaben fehlt ein Pool.
           */}
          {durchgefallen.length > 0 && (
            <button
              type="button"
              onClick={() =>
                setOffen({
                  titel: 'In keiner Ebene',
                  aufgaben: durchgefallen as unknown as Zeile[],
                })
              }
              className="mt-2 flex w-full items-baseline gap-2 rounded-lg border border-dashed border-pool-sofort/50 bg-[#fdeaea] px-2.5 py-1.5 text-left text-[12px]"
              title="Klicken zeigt, welche Aufgaben das sind"
            >
              <span className="font-semibold text-pool-sofort">⚠ In keiner Ebene</span>
              <strong className="tabular">{durchgefallen.length}</strong>
              <span className="text-ink2">
                eingeplant, aber in keinem Pool – niemand kann sie ziehen
              </span>
            </button>
          )}

          {/**
           * **Die zweite Lücke: im Vorrat, aber für niemanden vorgesehen** (Z-05).
           *
           * Steht **unter** „In keiner Ebene" und sieht anders aus, weil sie etwas anderes
           * sagt. Dort fehlt ein Pool – niemand kann die Aufgabe ziehen, das ist ein Defekt.
           * Hier liegt sie im Vorrat und ist ziehbar; es fehlt nur die Zuordnung, für wen sie
           * gedacht ist. Deshalb kein Rot, sondern die ruhige Fläche: eine Aufgabenliste, kein
           * Alarm.
           *
           * Und deshalb bleibt sie im Vorrat sichtbar – ausdrücklich entschieden (Nils,
           * 24.08.2026: *„ne 2 nicht, die sollen bleiben"*). Sie verschwindet nicht, sie wird
           * aufgeräumt.
           */}
          {ohneWunsch.length > 0 && (
            <button
              type="button"
              onClick={() =>
                setOffen({
                  titel: 'Ohne Wunsch-Entwickler',
                  aufgaben: ohneWunsch as unknown as Zeile[],
                  wunschSetzen: true,
                })
              }
              className="mt-2 flex w-full items-baseline gap-2 rounded-lg border border-dashed border-line2 bg-[#faf9f7] px-2.5 py-1.5 text-left text-[12px] hover:bg-[#f3f2ef]"
              title="Klicken zeigt die Liste – dort lassen sie sich im Bündel zuordnen"
            >
              <span className="font-semibold text-ink2">★ Ohne Wunsch-Entwickler</span>
              <strong className="tabular">{ohneWunsch.length}</strong>
              <span className="text-muted">
                im Vorrat, aber für niemanden vorgesehen – im Bündel zuordnen
              </span>
            </button>
          )}

          <p className="mt-2 text-[11px] text-muted">
            Unten die Spitze: der wichtigste Pool.{' '}
            <strong>Die Form ist die Stellung im Ablauf, die Füllung die Menge</strong> – im
            Verhältnis zur größten <em>arbeitenden</em> Ebene ({groesste}). Erledigtes steht oben
            als Zeile und fällt nach {FERTIG_TAGE} Tagen von selbst heraus.
            {mehrfach > 0 && (
              <>
                {' '}
                <strong>{mehrfach}</strong> {mehrfach === 1 ? 'Aufgabe liegt' : 'Aufgaben liegen'}{' '}
                in mehr als einem Pool und
                {mehrfach === 1 ? ' steht' : ' stehen'} deshalb in mehreren Ebenen.
              </>
            )}
          </p>
        </CardPad>
      </Card>

      {offen && (
        <div
          className="fixed inset-x-0 bottom-0 unter-leiste z-50 flex items-start justify-center bg-[rgb(23_22_20/0.38)] p-3 pt-[4vh] sm:p-6 sm:pt-[8vh]"
          onClick={(e) => e.target === e.currentTarget && setOffen(null)}
          role="dialog"
          aria-modal="true"
          aria-label={offen.titel}
        >
          <div className="flex max-h-[min(90vh,100%)] sm:max-h-[min(80vh,100%)] w-full min-w-0 max-w-[720px] flex-col rounded-card border border-line bg-surface shadow-card">
            <div className="flex items-center gap-2 border-b border-line px-[18px] py-3">
              <h3 className="text-sm font-bold">{offen.titel}</h3>
              <span className="text-[12px] text-muted">{offen.aufgaben.length}</span>
              <button
                type="button"
                onClick={() => setOffen(null)}
                className="ml-auto cursor-pointer rounded-lg px-2 py-1 text-ink2 hover:bg-[#f3f2ef]"
                aria-label="Schließen"
              >
                ✕
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-[18px] py-2.5">
              <TrichterListe aufgaben={offen.aufgaben} wunschSetzen={offen.wunschSetzen ?? false} />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

/**
 * Wonach die Liste sortiert wird. `alter` ist der Standard – das Älteste zuerst.
 *
 * Als **Typ** und nicht als Feld: die Spaltenköpfe stehen einzeln im Markup, niemand geht
 * über eine Liste. Das Feld daneben war deshalb nur eine Typquelle – und genau das hat der
 * Lint gemeldet (`only used as a type`).
 */
type Sortierung = 'alter' | 'status' | 'person' | 'fenster'

const SORT_LABELS: Record<Sortierung, string> = {
  alter: 'Alter',
  status: 'Status',
  person: 'Bearbeiter',
  fenster: 'Zeitfenster',
}

/** Reihenfolge der Fenster für die Sortierung – ohne Angabe zuletzt. */
const FENSTER_RANG: Record<string, number> = { jetzt: 0, tage5: 1, tage10: 2, monat: 3, quartal: 4 }

/**
 * Der Inhalt einer Ebene – **mit Alter, sortierbar, und mit der Zeitvergabe an derselben
 * Zeile** (19.08.2026).
 *
 * Nils, mit einem Bild der Pool-Liste: *„hier müsste noch das Alter mit rein. Dann kann ich
 * besser sortieren und die Zeiten vergeben."*
 *
 * ## Warum das Alter die Voreinstellung der Sortierung ist
 *
 * Weil es die Frage beantwortet, mit der man eine Pool-Liste öffnet: *was liegt hier am
 * längsten?* Die Reihenfolge aus dem Vorrat ist eine andere – dort sortiert
 * `poolSortierung` nach Zeitfenster, Priorität, Alter, also danach, **was als Nächstes
 * gezogen werden soll**. Hier wird nicht gezogen, hier wird eingeplant.
 *
 * ## Warum das Auswahlfeld hier steht und nicht im Detail
 *
 * *„Dann kann ich … die Zeiten vergeben"* – der Satz beschreibt einen Durchgang durch eine
 * Liste, nicht achtzehn Besuche im Aufgabendetail. Das Feld schreibt dasselbe wie dort
 * (`planungSetzen`), nur ohne den Weg dorthin.
 *
 * ## Warum die Zeilen hier noch einmal nachgeschlagen werden (21.08.2026)
 *
 * Nils: *„wenn ich in der Planung die Zeitfenster bearbeite … in den Aufgaben sind sie richtig,
 * aber im Popup stehen die neuen nicht drin."* Die Ebene, die der Trichter beim Klick in seinen
 * Zustand legt, ist eine **Momentaufnahme**: `useState` hält die Objekte fest, die es beim
 * Öffnen gab. Das Setzen lädt `tasks.all` neu, die Pyramide dahinter rechnet mit den neuen
 * Zahlen – nur das offene Popup zeigt weiter die alten Werte, bis man es schließt und wieder
 * aufmacht.
 *
 * Nachgeschlagen wird **je Zeile**, nicht die Liste neu gebildet: wer einen Durchgang macht,
 * soll seine Reihenfolge behalten. Eine Aufgabe, die durch das gesetzte Fenster in eine andere
 * Ebene gehört, bleibt deshalb bis zum Schließen stehen – aber mit ihrem neuen Wert.
 */
function TrichterListe({
  aufgaben,
  wunschSetzen,
}: {
  aufgaben: Zeile[]
  /** Nur die Z-05-Liste – anderswo wären Häkchen eine Auswahl ohne Aktion dahinter. */
  wunschSetzen?: boolean
}) {
  const queryClient = useQueryClient()
  const [sortierung, setSortierung] = useState<Sortierung>('alter')
  const [absteigend, setAbsteigend] = useState(false)
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(() => new Set())

  /** Dieselbe Grenze wie oben – eine Ebene tiefer im selben Trichter. */
  const { aufgaben: bestand } = useAufgabenDieserWelt()
  const frisch = useMemo(() => {
    const nachId = new Map(bestand.map((a) => [a.id, a as unknown as Zeile]))
    return aufgaben.map((a) => nachId.get(a.id) ?? a)
  }, [aufgaben, bestand])

  const verschieben = useMutation({
    mutationFn: (v: { id: string; zeitfenster: Zeitfenster | null }) =>
      trpc.tasks.planungSetzen.mutate({ id: v.id, zeitfenster: v.zeitfenster }),
    onSuccess: () => nebenBestandVerwerfen(queryClient),
  })

  /** Dieselbe Spalte noch einmal: Richtung drehen. Eine andere: von vorn, aufsteigend. */
  const sortSetzen = (wert: Sortierung) => {
    if (wert === sortierung) setAbsteigend((x) => !x)
    else {
      setSortierung(wert)
      setAbsteigend(false)
    }
  }

  const sortiert = [...frisch].sort((a, b) => {
    const richtung = absteigend ? -1 : 1
    switch (sortierung) {
      case 'alter':
        // Älteste zuerst: das größere Alter nach vorn.
        return (alterDerAufgabe(b) - alterDerAufgabe(a)) * richtung
      case 'status':
        return a.status.localeCompare(b.status, 'de') * richtung
      case 'person':
        return (a.assigneeName ?? 'zzz').localeCompare(b.assigneeName ?? 'zzz', 'de') * richtung
      case 'fenster':
        return (
          ((FENSTER_RANG[a.zeitfenster ?? ''] ?? 9) - (FENSTER_RANG[b.zeitfenster ?? ''] ?? 9)) *
          richtung
        )
    }
  })
  const sichtbar = sortiert.slice(0, POPUP_MAX)

  /**
   * **„Alle" heißt: alle sichtbaren.** Die Liste ist auf {@link POPUP_MAX} gedeckelt, und ein
   * Häkchen, das 300 Zeilen wählt, von denen man 50 sieht, ist eine Zusage, die man nicht
   * prüfen kann. Steht mehr dahinter, sagt die Leiste es dazu.
   */
  const alleGewaehlt = sichtbar.length > 0 && sichtbar.every((a) => gewaehlt.has(a.id))
  const umschalten = (id: string) =>
    setGewaehlt((v) => {
      const neu = new Set(v)
      if (neu.has(id)) neu.delete(id)
      else neu.add(id)
      return neu
    })

  return (
    <>
      {/**
       * **Die Kopfzeile ist die Sortierung.** Ein zweiter Klick auf dieselbe Spalte dreht die
       * Richtung – kein eigenes Menü, weil die Spalten ohnehin dastehen.
       */}
      <div className="mb-1 flex items-center gap-2 border-b border-line pb-1 text-[10.5px] text-muted">
        {wunschSetzen && (
          <input
            type="checkbox"
            checked={alleGewaehlt}
            onChange={() =>
              setGewaehlt(alleGewaehlt ? new Set() : new Set(sichtbar.map((a) => a.id)))
            }
            aria-label="Alle sichtbaren auswählen"
            className="shrink-0"
          />
        )}
        {(
          [
            ['alter', 'w-[46px] shrink-0 text-right'],
            ['status', 'w-[86px] shrink-0'],
          ] as const
        ).map(([wert, breite]) => (
          <span key={wert} className={breite}>
            <SortKnopf wert={wert} aktiv={sortierung} absteigend={absteigend} setzen={sortSetzen} />
          </span>
        ))}
        <span className="min-w-0 flex-1">Aufgabe</span>
        {(
          [
            ['person', 'w-[112px] shrink-0 text-right'],
            ['fenster', 'w-[104px] shrink-0 text-right'],
          ] as const
        ).map(([wert, breite]) => (
          <span key={wert} className={breite}>
            <SortKnopf wert={wert} aktiv={sortierung} absteigend={absteigend} setzen={sortSetzen} />
          </span>
        ))}
      </div>

      {sichtbar.map((a) => {
        const tage = alterDerAufgabe(a)
        return (
          <div
            key={a.id}
            className="flex items-center gap-2 border-b border-line py-1.5 text-[12.5px] last:border-b-0"
          >
            {wunschSetzen && (
              <input
                type="checkbox"
                checked={gewaehlt.has(a.id)}
                onChange={() => umschalten(a.id)}
                aria-label={`${a.title} auswählen`}
                className="shrink-0"
              />
            )}
            {/**
             * **Das Alter trägt eine Farbe, keine zweite Zahl.** Über 60 Tage ist die Schwelle,
             * die der Leitstand auch für „älteste" benutzt – zwei Schwellen für dasselbe wären
             * zwei Auslegungen von „liegt zu lange".
             */}
            <span
              className={`tabular w-[46px] shrink-0 text-right text-[11px] ${
                tage > 60 ? 'font-semibold text-pool-sofort' : 'text-muted'
              }`}
              title={`Seit ${entstanden(a).toLocaleDateString('de-DE')} im Bestand`}
            >
              {tage} T
            </span>
            <span className="w-[86px] shrink-0 truncate text-[11px] text-muted">
              {TASK_STATUS_LABELS[a.status as keyof typeof TASK_STATUS_LABELS] ?? a.status}
            </span>
            <AufgabenVerweis
              /**
               * **Die Liste bleibt offen** (19.08.2026). Vorher schloss sie sich, und wer eine
               * Aufgabe angesehen hatte, stand wieder vor der Pyramide und musste die Ebene neu
               * aufmachen. Das Detail legt sich darüber – es zu schließen bringt die Liste
               * zurück, samt Sortierung und Scrollstand.
               */
              aufgabeId={a.id}
              className="min-w-0 flex-1 cursor-pointer truncate text-left hover:underline"
              title={a.title}
            >
              {a.title}
            </AufgabenVerweis>
            <span className="w-[112px] shrink-0 truncate text-right text-[11px] text-muted">
              {a.assigneeName ?? 'niemand'}
            </span>
            <select
              value={a.zeitfenster ?? ''}
              onChange={(e) =>
                verschieben.mutate({
                  id: a.id,
                  zeitfenster: (e.target.value || null) as Zeitfenster | null,
                })
              }
              aria-label={`Zeitfenster von ${a.title}`}
              className="w-[104px] shrink-0 cursor-pointer rounded border border-line2 bg-surface px-1 py-px text-[10.5px]"
            >
              <option value="">– kein Fenster</option>
              {ZEITFENSTER.map((z: Zeitfenster) => (
                <option key={z} value={z}>
                  {ZEITFENSTER_LABELS[z]}
                </option>
              ))}
            </select>
          </div>
        )
      })}
      {wunschSetzen && (
        <WunschSammelLeiste
          ids={[...gewaehlt]}
          nichtSichtbar={aufgaben.length - sichtbar.length}
          fertig={() => setGewaehlt(new Set())}
        />
      )}
      {aufgaben.length > POPUP_MAX && (
        /**
         * **Das Ende steht dran.** Eine Liste, die einfach aufhört, liest sich wie eine
         * vollständige – bei 3.054 Erledigten wäre das die falsche Auskunft.
         */
        <p className="py-2 text-[11.5px] text-muted">
          Die ersten {POPUP_MAX} von {aufgaben.length}. Der ganze Bestand steht in „Alle Aufgaben".
        </p>
      )}
      {aufgaben.length === 0 && <p className="py-2 text-[12.5px] text-muted">Nichts hier.</p>}
    </>
  )
}

/** Ein Spaltenkopf, der sortiert. Das Dreieck zeigt die Richtung – nur an der aktiven Spalte. */
function SortKnopf({
  wert,
  aktiv,
  absteigend,
  setzen,
}: {
  wert: Sortierung
  aktiv: Sortierung
  absteigend: boolean
  setzen: (wert: Sortierung) => void
}) {
  const istAktiv = wert === aktiv
  return (
    <button
      type="button"
      onClick={() => setzen(wert)}
      aria-pressed={istAktiv}
      className={`cursor-pointer hover:text-ink ${istAktiv ? 'font-semibold text-ink2' : ''}`}
      title={`Nach ${SORT_LABELS[wert]} sortieren`}
    >
      {SORT_LABELS[wert]}
      {istAktiv && <span aria-hidden="true">{absteigend ? ' ▲' : ' ▼'}</span>}
    </button>
  )
}

/**
 * **Wunsch-Entwickler für die gewählten Aufgaben setzen**
 * ([Z-05](../../../../docs/phasen/zusatz-02-mein-tag.md#z-05--der-trichter-bekommt-ohne-wunsch-entwickler)).
 *
 * Nils, 24.08.2026: *„dass sich dadurch ein schönes Gefüge erstellt, wo die Entwickler nur die
 * Aufgaben/Pools sehen, wo ich oder der Leiter die für vorgesehen haben (meistens so 2–3
 * Leute)."* 55 Aufgaben einzeln im Detailfenster zu öffnen ist keine Arbeitsweise – deshalb
 * Häkchen und eine Leiste, die unten stehen bleibt.
 *
 * ## Warum sie erst erscheint, wenn etwas gewählt ist
 *
 * Eine dauerhaft sichtbare Aktionsleiste über einer Liste ohne Auswahl ist ein Knopf, der
 * nichts tut. Sie kommt mit der Auswahl und geht mit ihr.
 *
 * ## Warum die Namen Knöpfe sind und keine Auswahlliste
 *
 * *„meistens so 2–3 Leute"* – eine Mehrfachauswahl in einem `<select>` verlangt Strg-Klick und
 * zeigt danach nicht, was gewählt ist. Dieselbe Darstellung wie „Kommen in Frage" im
 * Anlegen-Dialog, damit man sie nicht zweimal lernen muss.
 */
function WunschSammelLeiste({
  ids,
  nichtSichtbar,
  fertig,
}: {
  ids: string[]
  /** Wie viele die Liste nicht zeigt – damit „alle" nicht mehr verspricht, als es hält. */
  nichtSichtbar: number
  fertig: () => void
}) {
  const queryClient = useQueryClient()
  const { personen } = usePersonenDieserWelt()
  const [wunsch, setWunsch] = useState<string[]>([])
  const [meldung, setMeldung] = useState<string | null>(null)

  const setzen = useMutation({
    mutationFn: () =>
      trpc.tasks.wunschSammelSetzen.mutate({ taskIds: ids, wunschPersonIds: wunsch }),
    onSuccess: (r) => {
      setMeldung(
        `${r.gesetzt} ${r.gesetzt === 1 ? 'Aufgabe' : 'Aufgaben'} zugeordnet` +
          (r.uebersprungen > 0 ? ` · ${r.uebersprungen} übersprungen` : ''),
      )
      setWunsch([])
      fertig()
      void queryClient.invalidateQueries()
    },
    onError: (e: unknown) => setMeldung(e instanceof Error ? e.message : 'Das ging nicht.'),
  })

  if (ids.length === 0) {
    return meldung ? <p className="py-2 text-[11.5px] text-good-text">{meldung}</p> : null
  }

  const waehlbar = personen.filter((p) => p.isActive && zaehltAlsEntwickler(p))

  return (
    <div className="sticky bottom-0 mt-2 rounded-lg border border-line2 bg-surface p-2.5 shadow-card">
      <div className="mb-1.5 flex flex-wrap items-baseline gap-x-2 text-[12px]">
        <strong>
          {ids.length} {ids.length === 1 ? 'Aufgabe' : 'Aufgaben'} gewählt
        </strong>
        <span className="text-muted">wer kommt dafür in Frage?</span>
        {nichtSichtbar > 0 && (
          /**
           * **Ehrlich über den Deckel.** „Alle" wählt die sichtbaren; wer 55 erwartet und 50
           * bekommt, hält die Differenz sonst für einen Fehler – oder merkt sie gar nicht.
           */
          <span className="text-[10.5px] text-muted">
            ({nichtSichtbar} weitere stehen unter der Liste und sind nicht mitgewählt)
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-1">
        {waehlbar.map((p) => {
          const drin = wunsch.includes(p.id)
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setWunsch((v) => (drin ? v.filter((x) => x !== p.id) : [...v, p.id]))}
              aria-pressed={drin}
              className={cn(
                'rounded-full px-2.5 py-1 text-[11.5px] font-semibold',
                drin ? 'bg-accent-soft text-accent-deep' : 'text-ink2 hover:bg-[#f3f2ef]',
              )}
            >
              {drin ? '★ ' : ''}
              {p.displayName}
            </button>
          )
        })}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={setzen.isPending} onClick={() => setzen.mutate()}>
          {wunsch.length === 0 ? 'Wünsche entfernen' : `Für ${wunsch.length} setzen`}
        </Button>
        <button
          type="button"
          onClick={fertig}
          className="rounded-lg px-2 py-1 text-[12px] text-ink2 hover:bg-[#f3f2ef]"
        >
          Auswahl aufheben
        </button>
        {/**
         * **Die leere Menge ist eine Aktion, kein Versehen.** Sie entfernt alle Wünsche – die
         * Umkehrung derselben Handbewegung. Der Knopftext sagt es, damit niemand „setzen"
         * drückt und Namen verliert.
         */}
        {meldung && <span className="text-[11.5px] text-ink2">{meldung}</span>}
      </div>
    </div>
  )
}
